import { useEffect, useContext } from 'react';
import { actions, StateContext } from '../../../state/index';
import { authWithAmazon } from '../../../utils/amazon';
import { replaceUrl, getPaypalOauthUrl } from '../../../utils/oauth';
import { logger } from '../../../utils/logging';
import { omit } from 'lodash/object';
import { get } from 'lodash/object';

import {
  ERROR_TYPES,
  PAYMENT_NETWORKS,
  LOGGING,
  TOKEN_TYPES,
} from '../../../utils/enums';

const boolIsReturningFromOauth = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const oauthCode = queryParams.get('oauthCode');
  const oauthError = queryParams.get('oauthError');
  return !!oauthCode || !!oauthError;
};

const resetPaywallUrl = () => {
  // remove oauth query params from url and set to original paywall url
  const paywallUrl = window.location.href.substring(
    0,
    window.location.href.includes('&oauthCode')
      ? window.location.href.indexOf('&oauthCode')
      : window.location.href.indexOf('&oauthError')
  );
  replaceUrl(paywallUrl);
};

export const OAuthFlow = ({ recipientLogin, children }) => {
  const [applicationState, dispatch] = useContext(StateContext);

  useEffect(() => {
    const { selectedPaymentNetwork, accountConfiguration } = applicationState;
    const login = async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const oauthCode = queryParams.get('oauthCode');
      const oauthError = queryParams.get('oauthError');

      const additionalLogData = {
        paymentNetwork: selectedPaymentNetwork,
        oauthQueryParams: {},
      };
      queryParams.forEach(
        (value, key) => (additionalLogData.oauthQueryParams[key] = value)
      );
      additionalLogData.oauthQueryParams = omit(
        additionalLogData.oauthQueryParams,
        ['refid', 'lastname', 'lng']
      ); // remove query params not related to oauth

      if (oauthCode) {
        try {
          const dataForRecipientLogin = {
            code: oauthCode,
            redirect_uri: `${window.location.origin}/`,
          };

          resetPaywallUrl();
          await recipientLogin({
            dataForRecipientLogin,
            skipDefaultPostEffects: false,
            additionalLogDataForDebugging: additionalLogData,
          });
        } catch (error) {
          console.log(error);
          // resetPaywallUrl();
        }
      } else if (oauthError) {
        resetPaywallUrl();

        const errorType = ERROR_TYPES.UI_ERRORS.OAUTH_DECLINED;

        const { configuration } = applicationState;
        dispatch(
          actions.modals.showUiErrorModal(configuration, {
            errorType,
            contentProps: {
              showChooseAnotherNetwork: true,
              actions: [
                {
                  // eslint-disable-next-line no-template-curly-in-string
                  label: 'Log In With ${paymentNetwork.displayName}',
                  onClick: login,
                },
              ],
            },
            buttonProps: {
              small: false,
              title: 'Retry Login',
            },
          })
        );

        logger({ event: LOGGING.EVENTS.OAUTH_DECLINED, ...additionalLogData });
      } else {
        /*
            Because we can't redirect the user back to their unique paywall url after returning from OAuthFlow, we cache the application state in session storage before initiating OAuthFlow and then use the cached state to initialize our application state when returning from OAuthFlow. We're not including the validation tokens in order to fetch new ones upon returning, in order to avoid expired tokens when calling recipientLogin and acceptance
        */

        const stateParam = {
          paywallUrl: window.location.href,
          applicationState: {
            selectedPaymentNetwork,
            tokens: {
              validatePayment: get(
                applicationState,
                `tokens.${TOKEN_TYPES.VALIDATE_PAYMENT}`
              ),
            },
          },
        };

        // TODO: try catch when trying to find client id in case the accounts that are returned from backend don't match up to the available payment networks returned on the payment
        const oauthClientId = accountConfiguration.clientId;
        // login with amazon uses an sdk for initiating oauth, whereas paypal we redirect to a REST endpoint
        switch (selectedPaymentNetwork) {
          case PAYMENT_NETWORKS.AMAZON_BALANCE:
          case PAYMENT_NETWORKS.EGIFT_BLACKHAWK_AMAZON:
            authWithAmazon(oauthClientId, stateParam);
            break;
          case PAYMENT_NETWORKS.PAYPAL_ADAPTIVE_PAYMENTS:
          case PAYMENT_NETWORKS.PAYPAL_PAYOUTS:
            window.location.href = getPaypalOauthUrl(oauthClientId, stateParam);
            break;
          default:
            return;
        }
      }
    };

    login();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children({
    isReturningFromOauth: boolIsReturningFromOauth(),
  });
};

export default OAuthFlow;
